/**
 * Phase 14 Plan 14-02 — typed wrappers around the 8 `/api/logsheets/approver` endpoints
 * defined in Plan 14-01.
 *
 * Mirrors the Phase 13 `logsheet-checker.ts` service module pattern:
 *  - Uses the singleton `api` axios instance (with JWT interceptor + Token-Expired handler).
 *  - Each function throws an `ApiError` (via `toApiError`) on non-2xx.
 *  - Query params passed via `params:` (axios serialises them).
 *  - CSV download uses responseType: 'blob' and returns a Blob.
 */

import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  ApproverApproveRequest,
  ApproverBulkApproveRequest,
  ApproverBulkApproveResult,
  ApproverDetail,
  ApproverHistoryItem,
  ApproverHistoryListQuery,
  ApproverPendingItem,
  ApproverPendingListQuery,
  ApproverRecentEntry,
  ApproverRejectRequest,
} from '@/types/logsheet-approver';
import type { PaginatedResult } from '@/types/roles';

/**
 * GET /api/logsheets/approver/pending — paginated queue of Checked entries.
 * Permission: logsheet.approver.review
 */
export async function getApproverPendingRequest(
  query: ApproverPendingListQuery = {},
): Promise<PaginatedResult<ApproverPendingItem>> {
  try {
    const { data } = await api.get<PaginatedResult<ApproverPendingItem>>(
      '/api/logsheets/approver/pending',
      {
        params: {
          ...(query.siteId !== undefined ? { siteId: query.siteId } : {}),
          ...(query.machineId !== undefined ? { machineId: query.machineId } : {}),
          ...(query.checkerId !== undefined ? { checkerId: query.checkerId } : {}),
          ...(query.fromDate !== undefined && query.fromDate !== ''
            ? { fromDate: query.fromDate }
            : {}),
          ...(query.toDate !== undefined && query.toDate !== '' ? { toDate: query.toDate } : {}),
          ...(query.status !== undefined ? { status: query.status } : {}),
          page: query.page ?? 1,
          pageSize: query.pageSize ?? 20,
        },
      },
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/approver/pending/:id — full detail for a single Checked entry.
 * Permission: logsheet.approver.review
 */
export async function getApproverPendingDetailRequest(id: number): Promise<ApproverDetail> {
  try {
    const { data } = await api.get<ApproverDetail>(`/api/logsheets/approver/pending/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/approver/pending/:id/recent-entries — last-5 comparison entries.
 * Permission: logsheet.approver.review
 */
export async function getApproverRecentEntriesRequest(
  id: number,
): Promise<readonly ApproverRecentEntry[]> {
  try {
    const { data } = await api.get<readonly ApproverRecentEntry[]>(
      `/api/logsheets/approver/pending/${String(id)}/recent-entries`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * POST /api/logsheets/approver/pending/:id/approve — approve a Checked entry.
 * Permission: logsheet.approver.approve
 */
export async function approverApproveRequest(
  id: number,
  body: ApproverApproveRequest,
): Promise<ApproverDetail> {
  try {
    const { data } = await api.post<ApproverDetail>(
      `/api/logsheets/approver/pending/${String(id)}/approve`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * POST /api/logsheets/approver/pending/:id/reject — reject with reason + remarks + routeTo.
 * Permission: logsheet.approver.reject
 */
export async function approverRejectRequest(
  id: number,
  body: ApproverRejectRequest,
): Promise<ApproverDetail> {
  try {
    const { data } = await api.post<ApproverDetail>(
      `/api/logsheets/approver/pending/${String(id)}/reject`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/approver/history — approver's own action history.
 * Permission: logsheet.approver.read-history
 */
export async function getApproverHistoryRequest(
  query: ApproverHistoryListQuery = {},
): Promise<PaginatedResult<ApproverHistoryItem>> {
  try {
    const { data } = await api.get<PaginatedResult<ApproverHistoryItem>>(
      '/api/logsheets/approver/history',
      {
        params: {
          ...(query.status !== undefined ? { status: query.status } : {}),
          ...(query.siteId !== undefined ? { siteId: query.siteId } : {}),
          ...(query.fromDate !== undefined && query.fromDate !== ''
            ? { fromDate: query.fromDate }
            : {}),
          ...(query.toDate !== undefined && query.toDate !== '' ? { toDate: query.toDate } : {}),
          page: query.page ?? 1,
          pageSize: query.pageSize ?? 20,
        },
      },
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/approver/history.csv — streaming CSV export.
 * Permission: logsheet.approver.read-history
 * Returns a Blob; caller triggers browser download.
 */
export async function getApproverHistoryCsvRequest(
  query: ApproverHistoryListQuery = {},
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>('/api/logsheets/approver/history.csv', {
      responseType: 'blob',
      params: {
        ...(query.status !== undefined ? { status: query.status } : {}),
        ...(query.siteId !== undefined ? { siteId: query.siteId } : {}),
        ...(query.fromDate !== undefined && query.fromDate !== ''
          ? { fromDate: query.fromDate }
          : {}),
        ...(query.toDate !== undefined && query.toDate !== '' ? { toDate: query.toDate } : {}),
      },
    });
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * POST /api/logsheets/approver/pending/bulk-approve — approve up to 100 entries.
 * Permission: logsheet.approver.bulk-approve
 */
export async function approverBulkApproveRequest(
  body: ApproverBulkApproveRequest,
): Promise<ApproverBulkApproveResult> {
  try {
    const { data } = await api.post<ApproverBulkApproveResult>(
      '/api/logsheets/approver/pending/bulk-approve',
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}
